import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

const statusConfig = {
  green: { label: 'Healthy', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  amber: { label: 'Monitor', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  red: { label: 'At Risk', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' }
};

export default function AccountHealth({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchAccountHealth(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const status = statusConfig[data.concentrationStatus] || statusConfig.green;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
            style={{ fontFamily: brand.typography?.headerFont }}>
          Account Health
        </h3>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
          {status.label}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className="text-2xl font-bold text-gray-900">{data.concentrationPct}%</p>
          <p className="text-xs text-gray-500 mt-1">Top campaign share</p>
          {data.topCampaignName && (
            <p className="text-xs text-gray-400 mt-0.5 truncate" title={data.topCampaignName}>
              {data.topCampaignName}
            </p>
          )}
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className="text-2xl font-bold text-gray-900">
            {data.activeCampaigns}<span className="text-sm text-gray-400">/{data.totalCampaigns}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">Active campaigns</p>
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50">
          <p className={`text-2xl font-bold ${data.wastedSpendTotal > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {data.currency}{data.wastedSpendTotal.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Wasted spend</p>
          {data.wastedCampaigns.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">
              {data.wastedCampaigns.length} campaign{data.wastedCampaigns.length !== 1 ? 's' : ''} with no conversions
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
